import { DOCUMENT } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { FullstoryService, fullstoryToken } from 'go-modules/services/fullstory/fullstory.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { ActivityListDataSource } from '@go-modules/services/activity-list-datasource/activity-list.datasource';
import type { ActivityFactory } from 'go-modules/models/activity/activity.factory';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ActivityEditorPanel,
	activityEditorPanelToken
} from 'go-modules/activity-editor-panel/activity-editor-panel.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { VIEWS } from '@go-modules/enums/views';
import { BehaviorSubject, Observable, Subject, combineLatest, take, tap, delay, merge, EMPTY } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	first,
	switchMap,
	map,
	takeUntil
} from 'rxjs/operators';
import { ViewStateDataSource } from '@go-modules/services/view-state-datasource/view-state.datasource';
import { SortOrder, SortOrderTranslations } from '@go-modules/enums/sort-order-main';
import { AvailabilityFilter, AvailabilityFilterTranslations } from '@go-modules/enums/availability-filter';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {
	ZeroStateChangedEvent
} from '@go-modules/components/data-source-zero-state/data-source-zero-state.component';
import {
	LicenseExpirationHandler
} from '@go-modules/services/license-expiration-handler/license-expiration-handler.service';
import type { License } from '@go-modules/interfaces/licenses';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { MenuComponent } from '@go-modules/components/menus/menu/menu.component';
import { GoSidepanelService } from '@go-modules/services/go-sidepanel/go-sidepanel.service';
import {
	ActivityReorderComponent
} from '@go-modules/components/sidepanels/activity-reorder/activity-reorder.component';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { ENVIRONMENTS } from '@go-modules/services/environment-vars/environments';
import { CustomSortTourService } from '@go-modules/tours/sort-activities-tour/custom-sort-tour.service';
import { UserService as NgxUserService } from '@go-modules/services/user/user.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { CopyActivitiesDialogComponent } from '@go-modules/components/dialogs/copy-activities-dialog/copy-activities-dialog.component';
import { NgxGroupService } from '@go-modules/services/group/group.service';
import { PromptsService } from '@go-modules/services/prompts/prompts.service';
import { ACTIVITY_SOURCES, ActivitySourceDialogComponent } from '../dialogs/activity-source-dialog/activity-source-dialog.component';
import * as dayjs from 'dayjs';
import { NgxFeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { NgxLicenseUpgradeService } from '../../services/license/license-upgrade/license-upgrade.service';
import { ProductTier } from '@go-modules/interfaces/licenses/product-tier';
import { InviteUsersDialogComponent } from '../dialogs/invite-users-dialog/invite-users-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { LibraryDialogComponent, LibraryDialogData } from '../dialogs/library-dialog/library-dialog.component';
import { focusElementWithFallback, getFocusReturnTarget } from '@go-modules/utilities';
import { ActivityPreviewEditorDialogService } from '../dialogs/activity-preview-editor/activity-preview-editor-dialog.service';
import type {
	ActivityPreviewEditorDialogCloseResult
} from '../dialogs/activity-preview-editor/activity-preview-editor-dialog.service';
import { ProductTranslationParams } from '@go-modules/interfaces';
import { ActiveBrandService } from '@go-modules/services/active-brand';

@Component({
	selector: 'ngx-folder-view',
	templateUrl: './folder-view.component.html',
	styleUrls: ['./folder-view.component.scss'],
	providers: [TranslatePipe],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class NgxFolderViewComponent implements OnInit, OnDestroy {
	@ViewChild(MenuComponent) public menuComponent: MenuComponent;
	@ViewChild('folderContainer', {read: ElementRef}) public folderContainer: ElementRef;
	public readonly views = VIEWS;
	public currentView$: Observable<VIEWS>;
	public dataSourceZeroState: ZeroStateChangedEvent = {isLoading: true, hasZeroState: false, errorCode: null };
	public isCreating$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public filterOptions: AvailabilityFilter[] = Object.values(AvailabilityFilter);
	public filterTranslations = AvailabilityFilterTranslations;
	public sortByOptions: SortOrder[] = Object.values(SortOrder);
	public sortOrder = SortOrder;
	public sortOrderTranslations = SortOrderTranslations;
	public hasActivities$: Observable<boolean>;
	public shouldShowFooterButtons$: Observable<boolean>;
	public form: UntypedFormGroup;
	public componentDestroyed$$ = new Subject();
	public canCreateActivity$$ = new BehaviorSubject<boolean>(false);
	public getActivityCreationDisabledMessage$$ = new BehaviorSubject<string>('');
	public canReorderActivities$: Observable<boolean>;
	public showReorderActivities$: Observable<boolean>;
	public environmentVarsService: EnvironmentVarsService;

	private tourDismissed$ = new Subject<void>();
	private hasFinishedSetup$ = new BehaviorSubject<boolean>(false);

	public translationParams: ProductTranslationParams;

	constructor (
		private ngxActivityService: NgxActivityService,
		private translatePipe: TranslatePipe,
		private licenseExpirationHandler: LicenseExpirationHandler,
		public activityListDataSource: ActivityListDataSource,
		public viewStateDataSource: ViewStateDataSource,
		public ngxGoToastService: NgxGoToastService,
		public cdr: ChangeDetectorRef,
		private ngxGoSidepanelService: GoSidepanelService,
		private customSortTourService: CustomSortTourService,
		private ngxUserService: NgxUserService,
		private modal: GoModalService,
		private dialog: MatDialog,
		private ngxGroupService: NgxGroupService,
		private promptsService: PromptsService,
		private featureFlag: NgxFeatureFlagService,
		private ngxLicenseUpgradeService: NgxLicenseUpgradeService,
		private routerService: RouterService,
		private titleService: Title,
		private translateService: TranslateService,
		private activityPreviewEditorDialog: ActivityPreviewEditorDialogService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) public userService: UserService,
		@Inject(fullstoryToken) private fullstoryService: FullstoryService,
		@Inject(activityToken) private Activity: ActivityFactory,
		@Inject(activityEditorPanelToken) private activityEditorPanel: ActivityEditorPanel,
		@Inject(DOCUMENT) private document: Document,
		private activeBrandService: ActiveBrandService
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public ngOnInit () {
		this.getLicenseAndValidate();

		this.translationParams = this.activeBrandService.getTranslationParams();
		this.updatePageTitle(this.selectedService.getGroup().name);

		this.currentView$ = this.viewStateDataSource.viewState$;
		this.hasActivities$ = this.activityListDataSource.connect()
			.pipe(map((activities) => activities.length > 0));
		this.shouldShowFooterButtons$ = combineLatest({
			hasActivities: this.hasActivities$,
			isFiltering: this.activityListDataSource.isFiltering$
		}).pipe(
			map(({hasActivities, isFiltering}) => {
				return (hasActivities || isFiltering) && this.hasInstructorRoleOrAbove();
			})
		);
		this.canReorderActivities$ = this.activityListDataSource.connect()
			.pipe(map((activities) => this.hasInstructorRoleOrAbove() && activities.length >= 2));
		this.showReorderActivities$ = this.canReorderActivities$.pipe(
			map((canReorderActivities) =>
				canReorderActivities && this.activityListDataSource.getCurrentSort() === this.sortOrder.DEFAULT
			)
		);

		// only COURSE_LEVEL lti can see folder-view
		// so if LTI then prompt to copy activities
		// if instructor and no current activities
		if (this.isLti() && this.hasInstructorRoleOrAbove()) {
			combineLatest({
				hasActivities: this.hasActivities$,
				isFiltering: this.activityListDataSource.isFiltering$
			})
				.pipe(
					first(),
					filter(({hasActivities, isFiltering}) => {
						const doesRequireSetup = !hasActivities && !isFiltering;

						if (!doesRequireSetup) {
							this.hasFinishedSetup$.next(true);
						}
						return doesRequireSetup;
					})
				)
				.subscribe(() => {
					this.promptToCopyActivities();
				});
		} else {
			this.hasFinishedSetup$.next(true);
		}
		this.setupSortTourListener();
		this.setupForm();
		this.selectedService.selectedSubject
			.asObservable()
			.pipe(
				map((selected: any) => selected.group.name),
				distinctUntilChanged(),
				takeUntil(this.componentDestroyed$$)
			)
			.subscribe((folderName) => this.updatePageTitle(folderName));


		this.hasFinishedSetup$.pipe(
			filter((hasFinishedSetup) => hasFinishedSetup),
			take(1),
			takeUntil(this.componentDestroyed$$)
		).subscribe(() => {
			this.promptsService.showTermsOrTourWhenRequired();
		});
	}

	public ngOnDestroy () {
		this.componentDestroyed$$.next(true);
		this.componentDestroyed$$.complete();
		this.canCreateActivity$$.complete();
		this.getActivityCreationDisabledMessage$$.complete();
	}

	public isLti (): boolean {
		return this.environmentVarsService.environmentIs(ENVIRONMENTS.LTI);
	}

	public setupForm () {
		this.form = new UntypedFormGroup({
			sortBy: new UntypedFormControl(this.activityListDataSource.getCurrentSort()),
			filterBy: new UntypedFormControl(this.activityListDataSource.getCurrentFilter())
		});

		// Observe datasource filter changed
		this.activityListDataSource.availabilityFilter$$.asObservable()
			.pipe(distinctUntilChanged(), takeUntil(this.componentDestroyed$$))
			.subscribe((_filter) => {
				this.form.patchValue({filterBy: _filter});
				this.cdr.detectChanges();
			});
		// Observe datasource sort changed
		this.activityListDataSource.sortBy$$.asObservable()
			.pipe(distinctUntilChanged(), takeUntil(this.componentDestroyed$$))
			.subscribe((_sortBy) => {
				this.form.patchValue({sortBy: _sortBy});
				this.cdr.detectChanges();
			});

		// Observe Changes
		this.form.get('sortBy').valueChanges
			.pipe(distinctUntilChanged(), takeUntil(this.componentDestroyed$$))
			.subscribe((selected) => this.activityListDataSource.sortBy(selected));
		this.form.get('filterBy').valueChanges
			.pipe(distinctUntilChanged(), takeUntil(this.componentDestroyed$$))
			.subscribe((selected) => this.activityListDataSource.setAvailabilityFilter(selected));
	}

	public goToActivity (activity) {
		this.selectedService.setActivity(this.Activity.model(activity));
		this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW, {activity_id: activity.activity_id});
	}

	private validateCanCreateActivity () {
		this.canCreateActivity$$.next(this.ngxActivityService.canCreateActivity());
		this.getActivityCreationDisabledMessage$$.next(this.ngxActivityService.getActivityCreationDisabledMessage());

		const license = this.selectedService.getLicense();
		if (license) {
			// TODO STAB-489 Consolidate ngx/ng1 License interfaces since we're already using them interchangeably
			this.licenseExpirationHandler.displayLicenseExpirationWarningToastIfNeeded(license as unknown as License);
		}
	}

	private setupSortTourListener () {
		this.showReorderActivities$
			.pipe(
				filter((show) => show === true),
				take(1),
				tap(() => this.customSortTourService.getTour().start(()=> {
					this.tourDismissed$.next();
				})),
				delay(10000),
				takeUntil(merge(this.tourDismissed$, this.componentDestroyed$$)),
				tap(() => {
					this.customSortTourService.getTour().end();
				})
			)
			.subscribe();
	}

	private getLicenseAndValidate () {
		this.selectedService.selectedSubject
			.asObservable()
			.pipe(
				takeUntil(this.componentDestroyed$$),
				filter((selected: any) => selected.group)
			)
			.subscribe((selected) => {
				if (this.featureFlag.isAvailable('AI_TRIAL') &&
					this.selectedService.getGroup().hasInstructorRole(true) &&
					selected.group.license_trial_exists &&
					selected.license?.salesforce_license.is_free_trial &&
					(!selected.license.is_active || dayjs().isAfter(dayjs.utc(selected.license.ends_at)))) {
					this.handleExpiredLicense(selected.license);
				}
				this.validateCanCreateActivity();
			});
	}

	private updatePageTitle (folderName: string): void {
		this.titleService.setTitle(
			this.translateService.instant('document-title_dashboard', { name: folderName, product: this.translationParams.product })
		);
	}

	private handleExpiredLicense (license: License) {
		this.ngxLicenseUpgradeService.upgradeOrPurchase(license.trial.previous_license, true, true, true)
			.pipe(
				switchMap(({requestUpgrade, shouldRestart}) => {
					if (shouldRestart) {
						this.handleExpiredLicense(license);
					} else if (requestUpgrade) {
						// Non license-admin requested an upgrade
						return this.ngxLicenseUpgradeService
							.handleLicenseUpgradeRequest(license.trial.previous_license.id);
					} else {
						// License admin successfully upgraded
						return EMPTY;
					}
				}),
				takeUntil(this.componentDestroyed$$)
			).subscribe();
	}

	public hasInstructorRoleOrAbove (): boolean {
		return this.selectedService.getGroup().hasInstructorRole(true) || this.userService.currentUser.is_root_user;
	}

	public openInviteModal () {
		InviteUsersDialogComponent.open(this.dialog, {
			data: {
				group: this.selectedService.getGroup()
			}
		});
	}

	public openAssignmentSourceModal () {
		const focusReturnTarget = getFocusReturnTarget(this.document);
		this.isCreating$.next(true);
		this.modal.open(ActivitySourceDialogComponent, true)
			.afterClosed().subscribe((source: ACTIVITY_SOURCES | undefined) => {
				if (source === ACTIVITY_SOURCES.NEW) {
					this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_ASSIGNMENT_SOURCE_NEW, {});
					this.ngxActivityService.getDefault()
						.subscribe({
							next: (defaultActivity) => {
								const activity =
									this.Activity.useAsTemplate(this.Activity.model(defaultActivity.activity));
								activity.populateEmptyActivityOnGroup(this.selectedService.getGroup());
								activity.is_default = true;

								const license = this.selectedService.getLicense();
								if (this.featureFlag.isAvailable('AI_DEFAULT') && license?.salesforce_license.tier_name === ProductTier.Plus) {
									activity.llm_definitions = defaultActivity.aiUseType?.defaultLlmDefinitions;
								}

								this.openActivityEditor(activity, focusReturnTarget);
							},
							error: () => this.resetActivityCreationState(focusReturnTarget)
						});
				} else if (source === ACTIVITY_SOURCES.LIBRARY || source === ACTIVITY_SOURCES.CREATE) {
					const data: LibraryDialogData = {
						filterType: [CONTENT_TYPES.ACTIVITIES],
						mode: MODES.SELECT,
						collectionManagement: true
					};
					if (source === ACTIVITY_SOURCES.CREATE) {
						this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_ASSIGNMENT_SOURCE_TEMPLATE, {});
						data.create = true;
						data.openToOrg = this.selectedService.getOrg().group_id;
					} else {
						this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_ASSIGNMENT_SOURCE_LIBRARY, {});
					}

					this.modal.open(LibraryDialogComponent, false, {
						data
					}).afterClosed().subscribe((item) => {
						if (item) {
							this.ngxActivityService.get(item.id, { includeTasks: true })
								.subscribe((activity) => {
									activity.group_id = this.selectedService.getGroup().group_id;
									const newActivity = this.Activity.model({...activity, activity_id: null});
									newActivity.parent_id = activity.activity_id;
									newActivity.usage = 0;
									newActivity.is_duplicate = false;
									this.ngxActivityService.save(newActivity).subscribe({
										next: (act) => {
											this.activitySavedSuccess(this.Activity.model(act));
										}
									});
								});
						} else {
							this.resetActivityCreationState(focusReturnTarget);
						}
					});
				} else {
					this.resetActivityCreationState(focusReturnTarget);
				}
			});
	}

	public sortActivities () {
		this.ngxGoSidepanelService.open(ActivityReorderComponent, {
			title: this.translatePipe.transform('activity-reorder-panel_title'),
			noFooter: true
		}).afterClosed().subscribe((result) => {
			if (result) {
				this.activityListDataSource.reload();
			}
		});
	}

	public openActivityEditor (activity: any, focusReturnTarget: HTMLElement | null = null) {
		if (this.featureFlag.isAvailable('ACTIVITY_PREVIEW_EDITOR_DIALOG')) {
			this.activityPreviewEditorDialog.open({
				mode: 'create',
				activityDraft: activity,
				group: this.selectedService.getGroup(),
				account: this.selectedService.getAccount(),
				org: this.selectedService.getOrg(),
				license: this.selectedService.getLicense(),
				environment: this.environmentVarsService.get(EnvironmentVarsService.VAR.ENVIRONMENT)
			}).then((dialogRef) => {
				dialogRef.afterClosed()
					.pipe(take(1))
					.subscribe((result: ActivityPreviewEditorDialogCloseResult) => {
						if (result == null || 'dismissed' in result) {
							this.resetActivityCreationState(focusReturnTarget);
							return;
						}

						this.activitySavedSuccess(this.Activity.model(result.activity), false);
					});
			}).catch(() => this.resetActivityCreationState(focusReturnTarget));
			return;
		}

		this.activityEditorPanel.open({
			user: this.userService.currentUser,
			group: this.selectedService.getGroup(),
			activity,
			orgSettings: this.selectedService.getOrg().org_settings,
			firstFocusSelector: '#activity-editor-name-input',
			headerOptions: {
				editMode: false
			}
		}).result
			.then(() => this.activitySavedSuccess(activity))
			.catch(() => this.resetActivityCreationState(focusReturnTarget));
	}

	public zeroStateChanged (dataSourceZeroState: ZeroStateChangedEvent) {
		this.dataSourceZeroState = dataSourceZeroState;
	}

	public openMenu (event: MouseEvent) {
		// open browser menu on shift right click
		if (event.button === 2 && event.shiftKey) {
			return;
		}
		if (this.hasInstructorRoleOrAbove() && this.canCreateActivity$$) {
			this.menuComponent.openMenu(event, this.folderContainer.nativeElement);
		}
	}

	private activitySavedSuccess (activity, showToast = true) {
		this.isCreating$.next(false);
		activity.owner_name = this.userService.currentUser.full_name;
		this.activityListDataSource.addActivity(activity);
		if (!showToast) {
			return;
		}

		this.ngxGoToastService.createToast({
			type: GoToastStatusType.SUCCESS,
			message: 'folder-view_activity_added'
		});
	}

	private resetActivityCreationState (focusReturnTarget: HTMLElement | null): void {
		this.isCreating$.next(false);
		focusElementWithFallback(focusReturnTarget);
	}

	private promptToCopyActivities () {
		this.ngxUserService.getUserGroups(this.userService.currentUser.user_id).subscribe((groups) => {
			const excludingCurrentGroup = groups.filter((group) => {
				return group.group_id !== this.selectedService.getGroup().group_id;
			});
			if (excludingCurrentGroup.length) {
				this.openCopyActivityModal(excludingCurrentGroup);
			} else {
				this.hasFinishedSetup$.next(true);
			}
		});
	}

	private openCopyActivityModal (availableFolders) {
		this.modal.open(CopyActivitiesDialogComponent, true, {
			data: {availableFolders}
		}).afterClosed().subscribe((copyFrom: number | null) => {
			if (copyFrom) {
				this.ngxGroupService.copyActivities(
					copyFrom,
					this.selectedService.getGroup().group_id
				).subscribe({
					next: () => {
						this.activityListDataSource.reload();
						this.hasFinishedSetup$.next(true);
					},
					error: () => {
						this.ngxGoToastService.createToast({
							type: GoToastStatusType.ERROR,
							message: 'folder-view_failed-to-copy'
						});
					}
				});
			} else {
				this.hasFinishedSetup$.next(true);
			}
		});
	}
}
